"use client";
import { motion } from "motion/react";
import Link from "next/link";
import imgEmma from "@/assets/Emma_tile.png";
import { AccentStar } from "@/shared/ui/accent-star";
import { PracticumGroupLogo, PracticumGroupLogoMark } from "@/shared/ui/practicum-logos";
import type { Slide } from "@/entities/slide";
import { GroupSlider } from "./GroupSlider";
import { CSS_VAR_HOVER_RGBA } from "../lib/constants";

export function GroupTile({
  compact,
  hovered,
  onHover,
  onLeave,
  homeLinkActive,
  radius,
  slideIndex,
  onSlideChange,
  slideColor,
  contentHidden,
  isAnimating,
  isPageScrolled,
  slides,
}: {
  compact: boolean;
  hovered: boolean;
  onHover: () => void;
  onLeave: () => void;
  homeLinkActive: boolean;
  radius: number;
  slideIndex: number;
  onSlideChange: (i: number) => void;
  slideColor: string | null;
  contentHidden: boolean;
  isAnimating: boolean;
  isPageScrolled: boolean;
  slides: Slide[];
}) {
  // Border-radius (50% circle), Mark/Full logo toggle, and scrolled mobile/desktop
  // logo toggle are all driven by CSS @container queries (slot wrapper for tile-size
  // queries; body "page" container for viewport-size queries). No JS state for
  // responsive flags — spec §6.4.

  const showEmma = !compact && [0, 3, 4, 5].includes(slideIndex);
  const hoverBg = compact
    ? CSS_VAR_HOVER_RGBA[slideColor || "var(--group)"] || "rgba(87,183,69,0.55)"
    : "rgba(255,255,255,0.55)";
  const hoverBorder = slideColor || "var(--group)";
  const bgTr = compact ? "opacity 0s" : "opacity 0.25s ease";
  const logoIsWhite = compact && hovered;

  // ── Logo vertical centering & content — only visible in compact (bar) mode ──
  // Horizontal centering via CSS: left: 50% + transform: translateX(-50%).
  // Four logo variants rendered as siblings; CSS @container picks visible one:
  //   - .gt-scrolled-mobile / .gt-scrolled-desktop — toggled by viewport (body "page"
  //     container, @container page (max-width: 767px)).
  //   - .gt-logo-mark / .gt-logo-full — toggled by slot size (@container max-width: 149px).
  const showScrolled = compact && isPageScrolled;

  const logoBaseStyle = {
    position: "absolute" as const,
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 10,
    transition: "top 0.2s ease, color 0.25s ease, opacity 0.25s ease",
    pointerEvents: "none" as const,
    color: logoIsWhite ? "#fff" : "var(--foreground)",
    opacity: compact ? 1 : 0,
  };

  return (
    // Tile div — border-radius (full vs 50% circle) toggled by CSS @container on
    // slot wrapper (see globals.css [data-group-tile] rules).
    <div
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
      className="relative cursor-pointer overflow-hidden [container-type:size]"
      data-group-tile=""
      data-compact={compact ? "true" : "false"}
      style={{
        "--tile-radius": `${radius}px`,
        border: `1px solid ${hovered ? hoverBorder : "var(--group)"}`,
        transition: "border-radius 0.35s ease, border 0.25s ease, width 0.35s ease, height 0.35s ease",
        width: "100%",
        height: "100%",
      } as React.CSSProperties}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          zIndex: 0,
          borderRadius: "inherit",
          opacity: hovered ? 0 : 1,
          backgroundColor: "rgba(255,255,255,0.55)",
          backdropFilter: isAnimating ? "none" : "blur(24px)",
          WebkitBackdropFilter: isAnimating ? "none" : "blur(24px)",
          boxShadow:
            "inset 0 0 0 1px rgba(255,255,255,0.4), 0 2px 16px rgba(1,33,104,0.04)",
          transition: `${bgTr}, backdrop-filter 0.1s ease`,
        }}
      />
      <div
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          zIndex: 0,
          borderRadius: "inherit",
          opacity: hovered ? 1 : 0,
          backgroundColor: hoverBg,
          backdropFilter: !compact || isAnimating ? "none" : "blur(24px)",
          WebkitBackdropFilter: !compact || isAnimating ? "none" : "blur(24px)",
          transition: `${bgTr}, backdrop-filter 0.1s ease`,
        }}
      />
      {homeLinkActive && (
        <Link
          href="/"
          prefetch
          aria-label="На главную"
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 5,
            pointerEvents: "auto",
            borderRadius: "inherit",
          }}
        />
      )}
      {!compact && !isAnimating && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: contentHidden ? 0 : 1 }}
          transition={{ duration: 0.25 }}
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: contentHidden ? "none" : "auto",
            zIndex: 1,
          }}
        >
          <GroupSlider
            activeIndex={slideIndex}
            onChangeIndex={onSlideChange}
            hovered={hovered}
            showEmma={showEmma}
            slides={slides}
          />
        </motion.div>
      )}
      <div
        className="hidden md:block"
        style={{
          position: "absolute",
          bottom: 0,
          right: 0,
          height: "80cqh",
          aspectRatio: "438/438",
          zIndex: 2,
          pointerEvents: "none",
          opacity: showEmma ? 1 : 0,
          transition: "opacity 0.35s ease",
          overflow: "hidden",
        }}
      >
        <img
          src={imgEmma.src}
          alt=""
          style={{
            position: "absolute",
            bottom: 0,
            left: "7.33%",
            height: "100%",
            width: "auto",
            maxWidth: "none",
            objectFit: "contain",
            objectPosition: "bottom",
          }}
        />
      </div>
      {showScrolled ? (
        <>
          <div
            className="gt-scrolled-mobile"
            style={{
              ...logoBaseStyle,
              top: "calc((var(--bar-h) - 26px) / 2)",
            }}
          >
            <svg
              width="22"
              height="26"
              viewBox="0 0 22 26"
              fill="none"
              style={{ display: "block" }}
            >
              <circle cx="5" cy="5" r="2.5" fill="currentColor" />
              <circle cx="17" cy="5" r="2.5" fill="currentColor" />
              <circle cx="5" cy="13" r="2.5" fill="currentColor" />
              <circle cx="17" cy="13" r="2.5" fill="currentColor" />
              <circle cx="5" cy="21" r="2.5" fill="currentColor" />
              <circle cx="17" cy="21" r="2.5" fill="currentColor" />
            </svg>
          </div>
          <div
            className="gt-scrolled-desktop"
            style={{
              ...logoBaseStyle,
              top: "calc((var(--bar-h) - 15px) / 2)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <AccentStar
                color={logoIsWhite ? "#fff" : "var(--group)"}
                size={10}
              />
              <span
                className="select-none whitespace-nowrap"
                style={{
                  fontFamily: "var(--font-content)",
                  fontWeight: 700,
                  fontSize: "var(--fluid-text-xs)",
                  lineHeight: "15px",
                  letterSpacing: "0.25px",
                  color: "currentColor",
                }}
              >
                Меню
              </span>
            </div>
          </div>
        </>
      ) : (
        <>
          <div
            className="gt-logo-mark"
            style={{
              ...logoBaseStyle,
              top: "calc((var(--bar-h) - 22px) / 2)",
            }}
          >
            <PracticumGroupLogoMark
              height={22}
              starColor={logoIsWhite ? "#fff" : undefined}
              color={logoIsWhite ? "#fff" : undefined}
            />
          </div>
          <div
            className="gt-logo-full"
            style={{
              ...logoBaseStyle,
              top: compact ? "calc((var(--bar-h) - 24px) / 2)" : "var(--fluid-space-xl)",
            }}
          >
            <PracticumGroupLogo
              height={24}
              starColor={logoIsWhite ? "#fff" : undefined}
            />
          </div>
        </>
      )}
    </div>
  );
}
